import { orderBy } from "lodash";
import * as React from "react";
import { Client, ClientBenchmarkingModel, ClientRoleModel, ClientService } from "@app/client";
import { DashboardModel, RoleModel } from "@app/user";
import { isEqual } from "lodash";
import { SSO } from "@app/client-account/models";
import { EndpointBindingType, SSOType } from "@app/sso-schemes";
import { ClientInsuranceModel, InsuranceService } from "@app/insurance";
import { ClientNetworkBenchmarkingDataRightsEnum, ClientSoftwareReleaseModel, ClientToApi } from "../../client/client";
import { Features } from "@app/feature-flags";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { JSX } from "react";

export interface DashboardsProps {
  // initializer
  getClient: (clientId: number) => void;

  // state getters
  authorizedDashboards: DashboardModel[];
  authorizedDomains: string[];
  authorizedRoles: ClientRoleModel[];
  authorizedInsurances: ClientInsuranceModel[];
  authorizedInsurancesIsLoading: boolean;
  availableInsurances: ClientInsuranceModel[];
  availableInsurancesIsLoading: boolean;
  availableRoles: RoleModel[];
  bulkUploadLimitHourWindow: number;
  bulkUploadRecordLimit: number;
  selectedReleaseYear: number;
  softwareReleases: ClientSoftwareReleaseModel[];
  clientBenchmarking: ClientBenchmarkingModel[];
  clientBenchmarkingLoading: boolean;
  clientId: number;
  clientName: string;
  createNewClientGuestLogins: boolean;
  crossProjectDbConnection: number;
  dateDeactivated: string;
  dbConnection: number;
  defaultUserRoles: ClientRoleModel[];
  disableAccessCacheReads: boolean;
  disableAccessCacheWrites: boolean;
  enterpriseComplianceDashboardDbConnection: number;
  holdSoftwareRelease: boolean;
  insurancesHaveErred: boolean;
  isActive: boolean;
  isQuestInternal: boolean;
  logoUrl: string;
  networkBenchmarkingDataRights: ClientNetworkBenchmarkingDataRightsEnum;
  networkBenchmarkingDataSource: boolean;
  networkServicingProviderReportLimit: number;
  outreachClientXid: string;
  outreachMetricsActivationDate: string;
  pciDashboardDbConnection: number;
  prospectMarketServicingProviderReportLimit: number;
  questViewerGuestLoginsAllowed: boolean;
  questViewerGuestUserRoles: ClientRoleModel[];
  resourceAdminGuestLoginsAllowed: boolean;
  resourceAdminGuestUserRoles: ClientRoleModel[];
  restrictedUserCreationEnabled: boolean;
  sfId: string;
  storageName: string;
  storageString: string;

  // state setters
  setAuthorizedDashboards: React.Dispatch<React.SetStateAction<DashboardModel[]>>;
  setAuthorizedDomains: React.Dispatch<React.SetStateAction<string[]>>;
  setAuthorizedRoles: React.Dispatch<React.SetStateAction<ClientRoleModel[]>>;
  setAuthorizedInsurances: React.Dispatch<React.SetStateAction<ClientInsuranceModel[]>>;
  setAuthorizedInsurancesIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setAvailableInsurancesIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setBulkUploadLimitHourWindow: React.Dispatch<React.SetStateAction<number>>;
  setBulkUploadRecordLimit: React.Dispatch<React.SetStateAction<number>>;
  setClientBenchmarking: React.Dispatch<React.SetStateAction<ClientBenchmarkingModel[]>>;
  setClientId: React.Dispatch<React.SetStateAction<number>>;
  setClientName: React.Dispatch<React.SetStateAction<string>>;
  setCreateNewClientGuestLogins: React.Dispatch<React.SetStateAction<boolean>>;
  setCrossProjectDbConnection: React.Dispatch<React.SetStateAction<number>>;
  // This is where setDateDeactivated would go if it were settable on the UI
  setDbConnection: React.Dispatch<React.SetStateAction<number>>;
  setDefaultUserRoles: React.Dispatch<React.SetStateAction<ClientRoleModel[]>>;
  setDisableAccessCacheReads: React.Dispatch<React.SetStateAction<boolean>>;
  setDisableAccessCacheWrites:React.Dispatch<React.SetStateAction<boolean>>;
  setEnterpriseComplianceDashboardDbConnection: React.Dispatch<React.SetStateAction<number>>;
  setHoldSoftwareRelease: React.Dispatch<React.SetStateAction<boolean>>;
  setInsurancesHaveErred: React.Dispatch<React.SetStateAction<boolean>>;
  setIsActive: React.Dispatch<React.SetStateAction<boolean>>;
  setIsQuestInternal: React.Dispatch<React.SetStateAction<boolean>>;
  setLogoUrl: React.Dispatch<React.SetStateAction<string>>;
  setNetworkServicingProviderReportLimit: React.Dispatch<React.SetStateAction<number>>;
  setNetworkBenchmarkingDataRights: React.Dispatch<React.SetStateAction<ClientNetworkBenchmarkingDataRightsEnum>>;
  setOutreachClientXid: React.Dispatch<React.SetStateAction<string>>;
  setOutreachMetricsActivationDate: React.Dispatch<React.SetStateAction<string>>;
  setPciDashboardDbConnection: React.Dispatch<React.SetStateAction<number>>;
  setProspectMarketServicingProviderReportLimit: React.Dispatch<React.SetStateAction<number>>;
  setQuestViewerGuestLoginsAllowed: React.Dispatch<React.SetStateAction<boolean>>;
  setQuestViewerGuestUserRoles: React.Dispatch<React.SetStateAction<ClientRoleModel[]>>;
  setResourceAdminGuestLoginsAllowed: React.Dispatch<React.SetStateAction<boolean>>;
  setResourceAdminGuestUserRoles: React.Dispatch<React.SetStateAction<ClientRoleModel[]>>;
  setRestrictedUserCreationEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedReleaseYear: React.Dispatch<React.SetStateAction<number>>;
  setSfId: React.Dispatch<React.SetStateAction<string>>;
  setSoftwareReleases: React.Dispatch<React.SetStateAction<ClientSoftwareReleaseModel[]>>;
  setStorageName: React.Dispatch<React.SetStateAction<string>>;
  setStorageString: React.Dispatch<React.SetStateAction<string>>;
  setValidateSSO: React.Dispatch<React.SetStateAction<boolean>>;

  // sso setters - used here only for validation - taken from the SSO object
  // we intentionally don't export the getters
  initializeSSO: (sso: SSO) => void;
  setSsoInitialState: React.Dispatch<React.SetStateAction<SSO>>;
  setSsoIsEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  setSsoSchemeName: React.Dispatch<React.SetStateAction<string>>;
  setSsoDisplayName: React.Dispatch<React.SetStateAction<string>>;
  setSsoType: React.Dispatch<React.SetStateAction<SSOType>>;
  setSsoCallbackPath: React.Dispatch<React.SetStateAction<string>>;
  setSsoNameIdentifierClaimType: React.Dispatch<React.SetStateAction<string>>;
  setSsoEmailClaimType: React.Dispatch<React.SetStateAction<string>>;
  setSsoIdpEntityId: React.Dispatch<React.SetStateAction<string>>;
  setSsoIdpSigningCertificateBase64: React.Dispatch<React.SetStateAction<string[]>>;
  setSsoIdpSignOnEndpointBindingType: React.Dispatch<React.SetStateAction<EndpointBindingType>>;
  setSsoIdpSingleSignOnEndpoint: React.Dispatch<React.SetStateAction<string>>;
  setSsoSpEntityId: React.Dispatch<React.SetStateAction<string>>;
  //setSsoSpMetadataPath: React.Dispatch<React.SetStateAction<string>>;
  setSsoSpEncryptionCertificateBase64: React.Dispatch<React.SetStateAction<string>>;
  setSsoSpRequireEncryptedAssertions: React.Dispatch<React.SetStateAction<boolean>>;
  setSsoSpSignAuthenticationRequests: React.Dispatch<React.SetStateAction<boolean>>;
  setSsoSpSigningCertificateBase64: React.Dispatch<React.SetStateAction<string>>;

  // validation
  changedFields: () => string[];
  formIsValid: boolean;
  fieldNameToVisibleName: { [key: string]: string };
  requiredFields: { [key: string]: boolean };
  validationFunctions: { [key: string]: () => boolean };
  validationObject: { [requiredKey: string]: boolean };

  // submitters
  activateClient: () => Promise<void>;
  createClient: () => Promise<Client>;
  deactivateClient: () => Promise<void>;
  updateClient: () => Promise<void>;
  createGuestLogins: () => Promise<void>;
}

type ClientProviderProps = {
  clientId?: number;
} & { children: React.ReactNode };

export const ClientContext = React.createContext<Partial<DashboardsProps>>({});

// these defaults are for validation - if these defaults are changed, also update ssoReducer.ts
const defaultNameIdentifierClaimType = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier";
const defaultEmailClaimType = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress";
const clientService = new ClientService();
const insuranceService = new InsuranceService();

/**
 * This context serves to fetch any existing client data from the API and store form changes to submit.
 * It DOES reflect a given client's form changes.
 */
export function ClientProvider(props: ClientProviderProps): JSX.Element {
  const [availableInsurancesIsLoading, setAvailableInsurancesIsLoading] = React.useState<boolean>(false);
  const [authorizedDashboards, setAuthorizedDashboards] = React.useState<DashboardModel[]>([]);
  const [authorizedDomains, setAuthorizedDomains] = React.useState<string[]>([]);
  const [authorizedInsurances, setAuthorizedInsurances] = React.useState<ClientInsuranceModel[]>([]);
  const [authorizedInsurancesIsLoading, setAuthorizedInsurancesIsLoading] = React.useState<boolean>(false);
  const [authorizedRoles, setAuthorizedRoles] = React.useState<ClientRoleModel[]>([]);
  const [bulkUploadLimitHourWindow, setBulkUploadLimitHourWindow] = React.useState<number>();
  const [bulkUploadRecordLimit, setBulkUploadRecordLimit] = React.useState<number>();
  const [softwareReleases, setSoftwareReleases] = React.useState<ClientSoftwareReleaseModel[]>([]);
  const [availableInsurances, setAvailableInsurances] = React.useState<ClientInsuranceModel[]>([]);
  const [availableRoles, setAvailableRoles] = React.useState<RoleModel[]>([]);
  const [clientBenchmarking, setClientBenchmarking] = React.useState<ClientBenchmarkingModel[]>([]);
  const [clientBenchmarkingLoading, setClientBenchmarkingLoading] = React.useState<boolean>(true);
  const [clientId, setClientId] = React.useState<number>(undefined);
  const [clientName, setClientName] = React.useState<string>("");
  const [createNewClientGuestLogins, setCreateNewClientGuestLogins] = React.useState(true);
  const [crossProjectDbConnection, setCrossProjectDbConnection] = React.useState<number>();
  const [dateDeactivated, setDateDeactivated] = React.useState<string>();
  const [dbConnection, setDbConnection] = React.useState<number>();
  const [defaultUserRoles, setDefaultUserRoles] = React.useState<ClientRoleModel[]>([]);
  const [disableAccessCacheReads, setDisableAccessCacheReads] = React.useState(false);
  const [disableAccessCacheWrites, setDisableAccessCacheWrites] = React.useState(false);
  const [enterpriseComplianceDashboardDbConnection, setEnterpriseComplianceDashboardDbConnection] = React.useState<number>();
  const [holdSoftwareRelease, setHoldSoftwareRelease] = React.useState(false);
  const [initialClientBenchmarking, setInitialClientBenchmarking] = React.useState<ClientBenchmarkingModel[]>([]);
  const [insurancesHaveErred, setInsurancesHaveErred] = React.useState(false)
  const [isActive, setIsActive] = React.useState(true);
  const [isQuestInternal, setIsQuestInternal] = React.useState(false);
  const [logoUrl, setLogoUrl] = React.useState("");
  const [networkBenchmarkingDataRights, setNetworkBenchmarkingDataRights] = React.useState<ClientNetworkBenchmarkingDataRightsEnum>(ClientNetworkBenchmarkingDataRightsEnum.NotFound);
  const [networkBenchmarkingDataSource, setNetworkBenchmarkingDataSource] = React.useState(false);
  const [networkServicingProviderReportLimit, setNetworkServicingProviderReportLimit] = React.useState<number>();
  const [outreachClientXid, setOutreachClientXid] = React.useState("");
  const [outreachMetricsActivationDate, setOutreachMetricsActivationDate] = React.useState("");
  const [pciDashboardDbConnection, setPciDashboardDbConnection] = React.useState<number>();
  const [prospectMarketServicingProviderReportLimit, setProspectMarketServicingProviderReportLimit] = React.useState<number>();
  const [questViewerGuestLoginsAllowed, setQuestViewerGuestLoginsAllowed] = React.useState(true);
  const [resourceAdminGuestUserRoles, setResourceAdminGuestUserRoles] = React.useState<ClientRoleModel[]>([]);
  const [resourceAdminGuestLoginsAllowed, setResourceAdminGuestLoginsAllowed] = React.useState(false);
  const [restrictedUserCreationEnabled, setRestrictedUserCreationEnabled] = React.useState(false);
  const [selectedReleaseYear, setSelectedReleaseYear] = React.useState(-1);
  const [sfId, setSfId] = React.useState("");
  const [storageName, setStorageName] = React.useState("");
  const [storageString, setStorageString] = React.useState("");
  // this is the rest of the SSO stuff, placed here for validation. The used values are in /client-account/account-context
  const [ssoInitialState, setSsoInitialState] = React.useState<SSO>();
  const [questViewerGuestUserRoles, setQuestViewerGuestUserRoles] = React.useState<ClientRoleModel[]>([]);
  const [validateSSO, setValidateSSO] = React.useState(true);
  const [ssoIsEnabled, setSsoIsEnabled] = React.useState(false);
  const [ssoSchemeName, setSsoSchemeName] = React.useState("");
  const [ssoDisplayName, setSsoDisplayName] = React.useState("");
  const [ssoType, setSsoType] = React.useState<SSOType>();
  const [ssoCallbackPath, setSsoCallbackPath] = React.useState("");
  const [ssoEmailClaimType, setSsoEmailClaimType] = React.useState(defaultEmailClaimType);
  const [ssoNameIdentifierClaimType, setSsoNameIdentifierClaimType] = React.useState(defaultNameIdentifierClaimType);
  const [ssoIdpEntityId, setSsoIdpEntityId] = React.useState("");
  const [ssoIdpSingleSignOnEndpoint, setSsoIdpSingleSignOnEndpoint] = React.useState("");
  const [ssoIdpSignOnEndpointBindingType, setSsoIdpSignOnEndpointBindingType] = React.useState<EndpointBindingType>(EndpointBindingType.post);
  const [ssoIdpSigningCertificateBase64, setSsoIdpSigningCertificateBase64] = React.useState<string[]>([]);
  const [ssoSpEntityId, setSsoSpEntityId] = React.useState("");
  //const [ssoSpMetadataPath, setSsoSpMetadataPath] = React.useState("");
  const [ssoSpRequireEncryptedAssertions, setSsoSpRequireEncryptedAssertions] = React.useState(false);
  const [ssoSpEncryptionCertificateBase64, setSsoSpEncryptionCertificateBase64] = React.useState("");
  const [ssoSpSignAuthenticationRequests, setSsoSpSignAuthenticationRequests] = React.useState(false);
  const [ssoSpSigningCertificateBase64, setSsoSpSigningCertificateBase64] = React.useState("");
  const featureFlags = React.useContext(FeatureFlagContext).enabledFeatureFlags;
  // other ssoSp fields are never required, so this context doesn't care

  const [validationObject, setValidationObject] = React.useState<{ [key: string]: boolean }>({});
  const initialClient = React.useRef<Client>(null);

  // for convenience
  React.useEffect(() => {
    if (props.clientId !== undefined) {
      getClient(props.clientId);
      getAvailableInsurances(props.clientId);
      getAuthorizedInsurances(props.clientId);
      getAvailableRoles(props.clientId);
    } else {
      getAvailableInsurances(); // if there is no current client (i.e., a new client), get all availble insurances
      getAvailableRoles();
      getNewClientDefaults();
    }
  }, []);

  React.useEffect(() => {
    if (props.clientId !== undefined && featureFlags.has(Features.NETWORK_BENCHMARKING)) {
      getClientBenchmarking(props.clientId);
    }
  }, [featureFlags]);

  // if the authorized roles are changed, make sure the default roles don't include any that were removed
  React.useEffect(() => {
    const newDefaultRoles: ClientRoleModel[] = [];
    const newResourceAdminRoles: ClientRoleModel[] = [];
    const newQuestViewerGuestRoles: ClientRoleModel[] = [];

    // we can't iterate over authorizedRoles to find the removed roles, because they were removed
    // and we check whether the defaults exist first so we don't accidentally set the multiselects to empty on page load
    // because listing defaultUserRoles as a dependency in a function that updates the defaultUserRoles would be problematic
    if (defaultUserRoles.length) {
      defaultUserRoles.forEach((role) => {
        if (authorizedRoles.find((r) => r.roleId === role.roleId)) {
          newDefaultRoles.push(role);
        }
      });
      setDefaultUserRoles(newDefaultRoles);
    }
    if (resourceAdminGuestUserRoles.length) {
      resourceAdminGuestUserRoles.forEach((role) => {
        if (authorizedRoles.find((r) => r.roleId === role.roleId)) {
          newResourceAdminRoles.push(role);
        }
      });
      setResourceAdminGuestUserRoles(newResourceAdminRoles);
    }
    if (questViewerGuestUserRoles.length) {
      questViewerGuestUserRoles.forEach((role) => {
        if (authorizedRoles.find((r) => r.roleId === role.roleId)) {
          newQuestViewerGuestRoles.push(role);
        }
      });
      setQuestViewerGuestUserRoles(newQuestViewerGuestRoles);
    }
  }, [authorizedRoles]);

  const getClient = (clientId: number) => {
    clientService.getClient(clientId).then((client) => {
      const dashboards = orderBy(client.dashboards, (d) => d.name, "asc");
      const domains = orderBy(client.domains, (d) => d.domain, "asc");
      const roles = orderBy(client.roles, (r) => r.name, "asc");
      const insurances = (client.authorizedInsurances) ? orderBy(client.authorizedInsurances, (i) => i.planName, "asc") : [];
      const softwareReleases = [...client.softwareReleases];

      const defaultUserRoles = orderBy(roles.filter((r) => r.userDefault === true), (r) => r.name, "asc");
      const resourceAdminGuestUserRoles = orderBy(roles.filter((r) => r.resourceAdminGuestDefault === true), (r) => r.name, "asc");
      const questViewerGuestUserRoles = orderBy(roles.filter((r) => r.questViewerGuestDefault === true), (r) => r.name, "asc");

      // eslint-disable-next-line
      initialClient.current = {
        ...client,
        authorizedInsurances: insurances,
        dashboards,
        defaultUserRoles,
        domains,
        questViewerGuestLoginsAllowed: client.questViewerGuestLoginsAllowed || false,
        questViewerGuestUserRoles,
        resourceAdminGuestLoginsAllowed: client.resourceAdminGuestLoginsAllowed || false,
        resourceAdminGuestUserRoles,
        restrictedUserCreationEnabled: client.restrictedUserCreationEnabled || false,
        roles,
        softwareReleases,
      }; // This is/can be/will be used for telling a user what they have updated

      setAuthorizedDashboards(dashboards);
      setAuthorizedDomains(domains.map((d) => d.domain));
      setAuthorizedRoles(roles);
      setBulkUploadLimitHourWindow(client.bulkUploadLimitHourWindow);
      setBulkUploadRecordLimit(client.bulkUploadRecordLimit);
      setDefaultUserRoles(defaultUserRoles);
      setDisableAccessCacheReads(client.disableAccessCacheReads);
      setDisableAccessCacheWrites(client.disableAccessCacheWrites)
      setAuthorizedInsurances(insurances);
      setResourceAdminGuestUserRoles(resourceAdminGuestUserRoles);
      setQuestViewerGuestUserRoles(questViewerGuestUserRoles);

      setHoldSoftwareRelease(client.holdSoftwareRelease || false);
      setSoftwareReleases(softwareReleases);
      setSelectedReleaseYear(softwareReleases[0].releaseYear); // select the most recent release year as the default

      setClientId(clientId);
      setClientName(client.name);
      setCrossProjectDbConnection(client.crossProjectDatabaseConnectionId)
      setDateDeactivated(client.dateDeactivated);
      setDbConnection(client.databaseConnectionId);
      setEnterpriseComplianceDashboardDbConnection(client.enterpriseComplianceDashboardDatabaseConnectionId);
      setPciDashboardDbConnection(client.pciDashboardDatabaseConnectionId)

      setResourceAdminGuestLoginsAllowed(client.resourceAdminGuestLoginsAllowed || false);
      setQuestViewerGuestLoginsAllowed(client.questViewerGuestLoginsAllowed || false);
      setRestrictedUserCreationEnabled(client.restrictedUserCreationEnabled || false);
      setIsActive(client.isActive);
      setIsQuestInternal(client.isQuestInternal);
      setLogoUrl(client.logoUrl);
      setOutreachClientXid(client.outreachClientXid);
      setOutreachMetricsActivationDate(client.outreachMetricsActivationDate);
      setSfId(client.sfAccountId);
      setStorageString(client.storageConnectionString);
      setStorageName(client.storageContainerName);
      setNetworkBenchmarkingDataRights(client.networkBenchmarkingDataRights);
      setNetworkBenchmarkingDataSource(client.networkBenchmarkingDataSource);
      setNetworkServicingProviderReportLimit(client.networkServicingProviderReportLimit);
      setProspectMarketServicingProviderReportLimit(client.prospectMarketServicingProviderReportLimit);
    });
  }

  const getClientBenchmarking = (clientId: number) => {
    clientService.getClientBenchmarkingData(clientId).then((data) => {
      if (data?.length) {
        setClientBenchmarking(data);
        setInitialClientBenchmarking(data);
      }

      setClientBenchmarkingLoading(false);
    });
  }

  const getAvailableInsurances = (clientId?: number) => {
    // load the available insurances for this client
    setAvailableInsurancesIsLoading(true);
    insuranceService.getAllInsurancesByClientId(clientId)
      .then((insurances) => {
        setAvailableInsurances((insurances) ? orderBy(insurances, (i) => i.planName, "asc") : []);
        setAvailableInsurancesIsLoading(false);
      })
      .catch(() => {
        setInsurancesHaveErred(true);
        setAvailableInsurancesIsLoading(false);
        setAuthorizedInsurancesIsLoading(false);
      });
  }

  const getAuthorizedInsurances = (clientId?: number) => {
    setAuthorizedInsurancesIsLoading(true);
    insuranceService.getAuthorizedInsurancesByClientId(clientId)
      .then((insurances) => {
        setAuthorizedInsurances((insurances) ? orderBy(insurances, (i) => i.planName, "asc") : []);
        setAuthorizedInsurancesIsLoading(false);
      })
      .catch(() => {
        setInsurancesHaveErred(true);
        setAvailableInsurancesIsLoading(false);
        setAuthorizedInsurancesIsLoading(false);
      });
  }

  const getAvailableRoles = (clientId?: number) => {
    clientService.getAvailableRoles(clientId).then((roles) => {
      setAvailableRoles(orderBy(roles, (r) => r.name, "asc"));
    });
  }

  const getNewClientDefaults = () => {
    clientService.getNewClientDefaults().then((defaults) => {
      setEnterpriseComplianceDashboardDbConnection(defaults.defaultDatabaseConnectionId);
      setPciDashboardDbConnection(defaults.defaultDatabaseConnectionId);
      setCrossProjectDbConnection(defaults.defaultDatabaseConnectionId);
      setDbConnection(defaults.defaultDatabaseConnectionId);
      setStorageString(defaults.defaultStorageConnectionString);
      setAuthorizedRoles(orderBy(defaults.defaultRoles.map<ClientRoleModel>((r) => ({ clientDefault: false, name: r.name, questViewerGuestDefault: false, resourceAdminGuestDefault: false, roleId: r.roleId, roleType: r.roleType, userDefault: false })), (r) => r.name, "asc"));
      setAuthorizedDashboards(orderBy(defaults.defaultDashboards, (d) => d.name, "asc"));
      setNetworkBenchmarkingDataRights(ClientNetworkBenchmarkingDataRightsEnum.NotFound);
      setNetworkBenchmarkingDataSource(false);
    });
  }

  const ssoIPOptionsRequired = (!!ssoIdpEntityId || !!ssoIdpSingleSignOnEndpoint || ssoIdpSigningCertificateBase64?.length > 0);
  const ssoOptionsRequired = (ssoNameIdentifierClaimType !== defaultNameIdentifierClaimType || ssoEmailClaimType !== defaultEmailClaimType || !!ssoDisplayName || ssoIPOptionsRequired);

  const requiredFields: { [key: string]: boolean } = {
    "authorizedDashboards": true,
    "authorizedDomains": true,
    "authorizedInsurances": false,
    "authorizedRoles": true,
    "clientName": true,
    "crossProjectDbConnection": true,
    "dbConnection": true,
    "enterpriseComplianceDashboardDbConnection": true,
    "pciDashboardDbConnection": true,
    "ssoDisplayName": ssoOptionsRequired,
    "ssoEmailClaimType": ssoOptionsRequired,
    "ssoIdpEntityId": ssoIPOptionsRequired,
    "ssoIdpSigningCertificateBase64": ssoIPOptionsRequired,
    "ssoIdpSingleSignOnEndpoint": ssoIPOptionsRequired,
    "ssoNameIdentifierClaimType": ssoOptionsRequired,
    "ssoSpEncryptionCertificateBase64": ssoSpRequireEncryptedAssertions,
    "ssoSpSigningCertificateBase64": ssoSpSignAuthenticationRequests,
    "storageName": true,
    "storageString": true,
  };

  const fieldNameToVisibleName: { [key: string]: string } = {
    "authorizedDashboards": "Authorized Dashboards",
    "authorizedDomains": "Authorized Domains",
    "authorizedInsurances": "Authorized Insurances",
    "authorizedRoles": "Authorized Roles",
    "clientName": "Profile - Name",
    "crossProjectDbConnection": "Cross-Project DB Connection String",
    "dbConnection": "DB Connection String",
    "enterpriseComplianceDashboardDbConnection": "Enterprise Compliance Dashboard DB Connection String",
    "outreachClientXid": "Outreach Client XID",
    "outreachMetricsActivationDate": "Outreach Metrics Activation Date",
    "pciDashboardDbConnection": "PCI Dashboard DB Connection String",
    "sfId": "SF Account ID",
    "ssoDisplayName": "SSO - Name",
    "ssoEmailClaimType": "Email Claim Type",
    "ssoIdpEntityId": "Entity Id",
    "ssoIdpSigningCertificateBase64": "Signing Certificates",
    "ssoIdpSingleSignOnEndpoint": "SSO Endpoint",
    "ssoNameIdentifierClaimType": "Name Identifier Claim Type",
    "ssoSpEncryptionCertificateBase64": "Encrypted Assertions Certificate",
    "ssoSpSigningCertificateBase64": "Authentication Request Signing Certificate",
    "storageName": "Storage Connection Name",
    "storageString": "Storage Connection String",
  }

  const validationFunctions: { [key: string]: () => boolean } = {
    "authorizedDashboards": () => authorizedDashboards.length > 0,
    "authorizedDomains": () => authorizedDomains.length > 0,
    "authorizedInsurances": () => authorizedInsurances.length > 0,
    "authorizedRoles": () => authorizedRoles.length > 0,
    "clientName": () => !!clientName,
    "crossProjectDbConnection": () => crossProjectDbConnection > 0,
    "dbConnection": () => dbConnection > 0,
    "enterpriseComplianceDashboardDbConnection": () => enterpriseComplianceDashboardDbConnection > 0,
    "outreachClientXid": () => !outreachClientXid || !!outreachClientXid.match(/^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$/),
    "outreachMetricsActivationDate": () => !outreachMetricsActivationDate || !!outreachMetricsActivationDate.match(/\d{4}-\d{2}-\d{2}/),
    "pciDashboardDbConnection": () => pciDashboardDbConnection > 0,
    "ssoDisplayName": () => !!ssoDisplayName,
    "ssoEmailClaimType": () => !!ssoEmailClaimType,
    "ssoIdpEntityId": () => !!ssoIdpEntityId,
    "ssoIdpSigningCertificateBase64": () => ssoIdpSigningCertificateBase64.length > 0,
    "ssoIdpSingleSignOnEndpoint": () => !!ssoIdpSingleSignOnEndpoint,
    "ssoNameIdentifierClaimType": () => !!ssoNameIdentifierClaimType,
    "ssoSpEncryptionCertificateBase64": () => !!ssoSpEncryptionCertificateBase64,
    "ssoSpSigningCertificateBase64": () => !!ssoSpSigningCertificateBase64,
    "ssoType": () => !!ssoType,
    // string.match returns null if there's no match and an array otherwise, so !! is fine. no risk of an empty array
    // the regex itself checks that the string is between 3 and 63 characters, starts with a letter, and contains only hyphens, lowercase letters, and numbers after, with no consecutive hyphens
    "storageName": () => !!storageName && !!storageName.match(/^(?=.{3,63}$)^[a-z][a-z0-9]+(-[a-z0-9]+)*$/),
    "storageString": () => !!storageString,
  }

  const formIsValid = React.useMemo(() => {
    const validation: { [key: string]: boolean } = {};
    const requiredKeys = Object.entries(requiredFields).filter((field) => field[1]).map((field) => field[0]);
    requiredKeys.forEach((field) => Object.assign(validation, { [field]: validationFunctions[field]() }));

    // add optional field but validation needed
    validation["outreachMetricsActivationDate"] = validationFunctions["outreachMetricsActivationDate"]();
    validation["outreachClientXid"] = validationFunctions["outreachClientXid"]();

    setValidationObject(validation);

    return Object.values(validation).every((item) => item);
  }, [
    authorizedDashboards,
    authorizedDomains,
    authorizedInsurances,
    authorizedRoles,
    clientName,
    crossProjectDbConnection,
    dbConnection,
    defaultUserRoles,
    enterpriseComplianceDashboardDbConnection,
    outreachClientXid,
    outreachMetricsActivationDate,
    pciDashboardDbConnection,
    questViewerGuestUserRoles,
    resourceAdminGuestUserRoles,
    sfId,
    ssoDisplayName,
    ssoEmailClaimType,
    ssoIdpEntityId,
    ssoIdpSigningCertificateBase64,
    ssoIdpSingleSignOnEndpoint,
    ssoNameIdentifierClaimType,
    ssoSpEncryptionCertificateBase64,
    ssoSpSigningCertificateBase64,
    ssoType,
    storageName,
    storageString,
  ]);

  const changedFields = () => {
    if (!initialClient.current) {
      return [];
    }
    const domainList = authorizedDomains.map((ad) => ({ domain: ad }));
    const clientModel: Client = {
      authorizedInsurances,
      bulkUploadLimitHourWindow,
      bulkUploadRecordLimit,
      clientId,
      crossProjectDatabaseConnectionId: crossProjectDbConnection,
      dashboards: authorizedDashboards,
      databaseConnectionId: dbConnection,
      dateDeactivated,
      defaultUserRoles,
      disableAccessCacheReads,
      disableAccessCacheWrites,
      domains: domainList,
      enterpriseComplianceDashboardDatabaseConnectionId: enterpriseComplianceDashboardDbConnection,
      holdSoftwareRelease,
      isActive,
      isQuestInternal,
      logoUrl,
      name: clientName,
      networkBenchmarkingDataRights,
      networkBenchmarkingDataSource,
      networkServicingProviderReportLimit,
      outreachClientXid,
      outreachMetricsActivationDate,
      pciDashboardDatabaseConnectionId: pciDashboardDbConnection,
      prospectMarketServicingProviderReportLimit,
      questViewerGuestLoginsAllowed,
      questViewerGuestUserRoles,
      resourceAdminGuestLoginsAllowed,
      resourceAdminGuestUserRoles,
      restrictedUserCreationEnabled,
      roles: authorizedRoles,
      sfAccountId: sfId,
      softwareReleases,
      storageConnectionString: storageString,
      storageContainerName: storageName,
    };

    const clientKeys = Object.keys(clientModel);
    const changed: string[] = [];
    clientKeys.forEach((key) => {
      if (!isEqual(clientModel[key as keyof Client], initialClient.current[key as keyof Client])) {
        changed.push(key);
      }
    });

    if (validateSSO && (ssoIPOptionsRequired || ssoOptionsRequired)) {
      const ssoModel: SSO = {
        callbackPath: ssoCallbackPath,
        displayName: ssoDisplayName,
        emailClaimType: ssoEmailClaimType,
        idpEntityId: ssoIdpEntityId,
        idpSignOnEndpointBindingType: ssoIdpSignOnEndpointBindingType,
        idpSigningCertificatesBase64: ssoIdpSigningCertificateBase64,
        idpSingleSignOnEndpoint: ssoIdpSingleSignOnEndpoint,
        isEnabled: ssoIsEnabled,
        nameIdentifierClaimType: ssoNameIdentifierClaimType,
        schemeName: ssoSchemeName,
        spEncryptionCertificateBase64: ssoSpEncryptionCertificateBase64,
        spEntityId: ssoSpEntityId,
        //spMetadataPath: ssoSpMetadataPath,
        spRequireEncryptedAssertions: ssoSpRequireEncryptedAssertions,
        spSignAuthenticationRequests: ssoSpSignAuthenticationRequests,
        spSigningCertificateBase64: ssoSpSigningCertificateBase64,
        ssoType,
      }

      const ssoKeys = Object.keys(ssoModel);
      ssoKeys.forEach((key) => {
        if (!isEqual(ssoModel[key as keyof SSO], (ssoInitialState ? ssoInitialState[key as keyof SSO] : ""))) {
          changed.push(key);
        }
      });
    }

    if (featureFlags.has(Features.NETWORK_BENCHMARKING) && !isEqual(initialClientBenchmarking, clientBenchmarking)) {
      changed.push("clientBenchmarking");
    }

    return changed;
  }

  const initializeSSO = (sso: SSO) => {
    setSsoInitialState(sso);
    setSsoIsEnabled(sso.isEnabled);
    setSsoSchemeName(sso.schemeName);
    setSsoCallbackPath(sso.callbackPath);
    setSsoDisplayName(sso.displayName);
    setSsoNameIdentifierClaimType(sso.nameIdentifierClaimType);
    setSsoEmailClaimType(sso.emailClaimType);
    setSsoType(sso.ssoType);
    setSsoIdpEntityId(sso.idpEntityId);
    setSsoIdpSigningCertificateBase64(sso.idpSigningCertificatesBase64);
    setSsoIdpSignOnEndpointBindingType(sso.idpSignOnEndpointBindingType);
    setSsoIdpSingleSignOnEndpoint(sso.idpSingleSignOnEndpoint);
    setSsoSpEncryptionCertificateBase64(sso.spEncryptionCertificateBase64);
    setSsoSpEntityId(sso.spEntityId);
    setSsoSpRequireEncryptedAssertions(sso.spRequireEncryptedAssertions);
    setSsoSpSignAuthenticationRequests(sso.spSignAuthenticationRequests);
    setSsoSpSigningCertificateBase64(sso.spSigningCertificateBase64);
  }

  const activateClient = async () => {
    await clientService.activateClient(clientId);
  }

  const deactivateClient = async () => {
    await clientService.deactivateClient(clientId);
  }

  const createGuestLogins = async () => {
    await clientService.createGuestLogins(clientId);
  }

  const createClient = async () => {
    const domainList = authorizedDomains.map((ad) => ({ domain: ad }));

    authorizedRoles.forEach((ar) => {
      /* if defaultUserRoles contains this authorized role, set ar.userDefault = true; if it doesn't, then it's false if  */
      const foundUserDefaultItem = defaultUserRoles.find((dur) => dur.roleId === ar.roleId);
      ar.userDefault = (!!foundUserDefaultItem)
        ? true
        : (ar.userDefault == null)
          ? null
          : false;

      const foundResourceAdminItem = resourceAdminGuestUserRoles.find((rag) => rag.roleId == ar.roleId);
      ar.resourceAdminGuestDefault = (!!foundResourceAdminItem)
        ? true
        : (ar.resourceAdminGuestDefault == null)
          ? null
          : false;

      const foundQuestViewerItem = questViewerGuestUserRoles.find((tgu) => tgu.roleId == ar.roleId);
      ar.questViewerGuestDefault = (!!foundQuestViewerItem)
        ? true
        : (ar.questViewerGuestDefault == null)
          ? null
          : false;

      return ar;
    });

    const clientModel: Client = {
      authorizedInsurances,
      bulkUploadLimitHourWindow: bulkUploadLimitHourWindow,
      bulkUploadRecordLimit: bulkUploadRecordLimit,
      clientId, // should be undefined here anyway
      crossProjectDatabaseConnectionId: crossProjectDbConnection,
      dashboards: authorizedDashboards,
      databaseConnectionId: dbConnection,
      dateDeactivated: undefined,
      defaultUserRoles,
      disableAccessCacheReads,
      disableAccessCacheWrites,
      domains: domainList,
      enterpriseComplianceDashboardDatabaseConnectionId: enterpriseComplianceDashboardDbConnection,
      holdSoftwareRelease,
      isActive,
      isQuestInternal,
      logoUrl: logoUrl,
      name: clientName,
      networkBenchmarkingDataRights,
      networkBenchmarkingDataSource,
      networkServicingProviderReportLimit,
      outreachClientXid,
      outreachMetricsActivationDate,
      pciDashboardDatabaseConnectionId: pciDashboardDbConnection,
      prospectMarketServicingProviderReportLimit,
      questViewerGuestLoginsAllowed,
      questViewerGuestUserRoles,
      resourceAdminGuestLoginsAllowed,
      resourceAdminGuestUserRoles,
      restrictedUserCreationEnabled,
      roles: authorizedRoles,
      sfAccountId: sfId,
      softwareReleases,
      storageConnectionString: storageString,
      storageContainerName: storageName,
    };

    if (formIsValid) {
      const newClient = await clientService.createClient(clientModel);
      if (createNewClientGuestLogins) {
        await clientService.createGuestLogins(newClient.clientId);
      }
      return newClient;
    }
  }

  const updateClient = async () => {
    const changes: string[] = changedFields();

    if (featureFlags.has(Features.NETWORK_BENCHMARKING) && changes.includes("clientBenchmarking")) {
      const filteredClientBenchmarking = clientBenchmarking.filter(x => !!x.stateAbbreviations.length);

      await clientService.updateClientBenchmarkingData(clientId, filteredClientBenchmarking);

      if (changes.length === 1) {
        return;
      }
    }

    const domainList = authorizedDomains.map((ad) => ({ domain: ad }));

    const initialReleasedReleaseIds = initialClient.current?.softwareReleases.filter((sr) => sr.releasedToClient).map((sr) => sr.softwareReleaseId) || [];
    const addedSoftwareReleaseIds = softwareReleases.filter((sr) => sr.releasedToClient && !initialReleasedReleaseIds.includes(sr.softwareReleaseId)).map((sr) => sr.softwareReleaseId);
    const removedSoftwareReleaseIds = softwareReleases.filter((sr) => !sr.releasedToClient && initialReleasedReleaseIds.includes(sr.softwareReleaseId)).map((sr) => sr.softwareReleaseId);

    authorizedRoles.forEach((ar) => {
      /* if defaultUserRoles contains this authorized role, set ar.userDefault = true; if it doesn't, then it's false if  */
      const foundUserDefaultItem = defaultUserRoles.find((dur) => dur.roleId === ar.roleId);
      ar.userDefault = (!!foundUserDefaultItem)
        ? true
        : (ar.userDefault == null)
          ? null
          : false;

      const foundResourceAdminItem = resourceAdminGuestUserRoles.find((rag) => rag.roleId == ar.roleId);
      ar.resourceAdminGuestDefault = (!!foundResourceAdminItem)
        ? true
        : (ar.resourceAdminGuestDefault == null)
          ? null
          : false;

      const foundQuestViewerItem = questViewerGuestUserRoles.find((tgu) => tgu.roleId == ar.roleId);
      ar.questViewerGuestDefault = (!!foundQuestViewerItem)
        ? true
        : (ar.questViewerGuestDefault == null)
          ? null
          : false;

      return ar;
    });

    const clientModel: ClientToApi = {
      addedSoftwareReleaseIds: addedSoftwareReleaseIds,
      authorizedInsurances,
      bulkUploadLimitHourWindow,
      bulkUploadRecordLimit,
      clientId,
      crossProjectDatabaseConnectionId: crossProjectDbConnection,
      dashboards: authorizedDashboards,
      databaseConnectionId: dbConnection,
      dateDeactivated,
      disableAccessCacheReads,
      disableAccessCacheWrites,
      domains: domainList,
      enterpriseComplianceDashboardDatabaseConnectionId: enterpriseComplianceDashboardDbConnection,
      holdSoftwareRelease,
      isActive,
      isQuestInternal,
      logoUrl,
      name: clientName,
      networkBenchmarkingDataRights,
      networkBenchmarkingDataSource,
      networkServicingProviderReportLimit,
      outreachClientXid,
      outreachMetricsActivationDate,
      pciDashboardDatabaseConnectionId: pciDashboardDbConnection,
      prospectMarketServicingProviderReportLimit,
      questViewerGuestLoginsAllowed,
      removedSoftwareReleaseIds,
      resourceAdminGuestLoginsAllowed,
      restrictedUserCreationEnabled,
      roles: authorizedRoles,
      sfAccountId: sfId,
      storageConnectionString: storageString,
      storageContainerName: storageName
    };

    if (formIsValid) {
      await clientService.updateClient(clientModel);
    }
  }

  return (
    <ClientContext.Provider
      value={{
        activateClient,
        authorizedDashboards,
        authorizedDomains,
        authorizedInsurances,
        authorizedInsurancesIsLoading,
        authorizedRoles,
        availableInsurances,
        availableInsurancesIsLoading,
        availableRoles,
        bulkUploadLimitHourWindow,
        bulkUploadRecordLimit,
        changedFields,
        clientBenchmarking,
        clientBenchmarkingLoading,
        clientId,
        clientName,
        createClient,
        createGuestLogins,
        createNewClientGuestLogins,
        crossProjectDbConnection,
        dateDeactivated,
        dbConnection,
        deactivateClient,
        defaultUserRoles,
        disableAccessCacheReads,
        disableAccessCacheWrites,
        enterpriseComplianceDashboardDbConnection,
        fieldNameToVisibleName,
        formIsValid,
        getClient,
        holdSoftwareRelease,
        initializeSSO,
        insurancesHaveErred,
        isActive,
        isQuestInternal,
        logoUrl,
        networkBenchmarkingDataRights,
        networkBenchmarkingDataSource,
        networkServicingProviderReportLimit,
        outreachClientXid,
        outreachMetricsActivationDate,
        pciDashboardDbConnection,
        prospectMarketServicingProviderReportLimit,
        questViewerGuestLoginsAllowed,
        questViewerGuestUserRoles,
        requiredFields,
        resourceAdminGuestLoginsAllowed,
        resourceAdminGuestUserRoles,
        restrictedUserCreationEnabled,
        selectedReleaseYear,
        setAuthorizedDashboards,
        setAuthorizedDomains,
        setAuthorizedInsurances,
        setAuthorizedRoles,
        setBulkUploadLimitHourWindow,
        setBulkUploadRecordLimit,
        setClientBenchmarking,
        setClientName,
        setCreateNewClientGuestLogins,
        setCrossProjectDbConnection,
        setDbConnection,
        setDefaultUserRoles,
        setDisableAccessCacheReads,
        setDisableAccessCacheWrites,
        setEnterpriseComplianceDashboardDbConnection,
        setHoldSoftwareRelease,
        setIsActive,
        setIsQuestInternal,
        setLogoUrl,
        setNetworkBenchmarkingDataRights,
        setNetworkServicingProviderReportLimit,
        setOutreachClientXid,
        setOutreachMetricsActivationDate,
        setPciDashboardDbConnection,
        setProspectMarketServicingProviderReportLimit,
        setQuestViewerGuestLoginsAllowed,
        setQuestViewerGuestUserRoles,
        setResourceAdminGuestLoginsAllowed,
        setResourceAdminGuestUserRoles,
        setRestrictedUserCreationEnabled,
        setSelectedReleaseYear,
        setSfId,
        setSoftwareReleases,
        setSsoCallbackPath,
        setSsoDisplayName,
        setSsoEmailClaimType,
        setSsoIdpEntityId,
        setSsoIdpSignOnEndpointBindingType,
        setSsoIdpSigningCertificateBase64,
        setSsoIdpSingleSignOnEndpoint,
        setSsoInitialState,
        setSsoIsEnabled,
        setSsoNameIdentifierClaimType,
        setSsoSchemeName,
        setSsoSpEncryptionCertificateBase64,
        setSsoSpEntityId,
        setSsoSpRequireEncryptedAssertions,
        setSsoSpSignAuthenticationRequests,
        setSsoSpSigningCertificateBase64,
        setSsoType,
        setStorageName,
        setStorageString,
        setValidateSSO,
        sfId,
        softwareReleases,
        storageName,
        storageString,
        updateClient,
        validationFunctions,
        validationObject,
      }}
    >
      {props.children}
    </ClientContext.Provider>
  );
}
